import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Input, Button, Space, message } from 'antd';

const addOneMonth = (dateStr) => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) {
    return dateStr;
  }
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 10);
};

const AxisEditor = ({ axisConfig, onSave, onClose }) => {
  const { t } = useTranslation();

  const [name, setName] = useState(axisConfig.name || t('tools.timeline.title'));
  const [startDate, setStartDate] = useState(axisConfig.startDate);
  const [endDate, setEndDate] = useState(axisConfig.endDate);
  const [months, setMonths] = useState(
    (axisConfig.months || []).map((m, idx) => ({
      id: m.id || `month_${idx + 1}`,
      date: m.date,
      label: m.label,
    }))
  );

  const canSave = useMemo(() => {
    if (!startDate || !endDate || startDate >= endDate) {
      return false;
    }
    return true;
  }, [startDate, endDate]);

  const updateMonth = (id, patch) => {
    setMonths((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };

  const addMonth = () => {
    setMonths((prev) => [
      ...prev,
      {
        id: `month_${Date.now()}`,
        date: startDate,
        label: t('tools.timeline.newMonthLabel'),
      },
    ]);
  };

  const removeMonth = (id) => {
    setMonths((prev) => prev.filter((m) => m.id !== id));
  };

  const handleSave = () => {
    if (!canSave) {
      message.error(t('tools.timeline.axisSaveInvalidRange'));
      return;
    }

    const startMs = new Date(startDate).getTime();
    let effectiveEndDate = endDate;
    let endMs = new Date(effectiveEndDate).getTime();

    const normalizedMonths = months
      .map((m) => ({
        ...m,
        label: (m.label || '').trim(),
        date: m.date || '',
      }))
      .filter((m) => m.date && m.label)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const latestMonthDate = normalizedMonths.length > 0
      ? normalizedMonths[normalizedMonths.length - 1].date
      : null;

    if (latestMonthDate) {
      const latestMonthMs = new Date(latestMonthDate).getTime();
      if (Number.isFinite(latestMonthMs) && latestMonthMs >= endMs) {
        effectiveEndDate = addOneMonth(latestMonthDate);
        endMs = new Date(effectiveEndDate).getTime();
      }
    }

    const boundedMonths = normalizedMonths.filter((m) => {
      const ms = new Date(m.date).getTime();
      return Number.isFinite(ms) && ms >= startMs && ms < endMs;
    });

    const safeMonths = boundedMonths.length > 0
      ? boundedMonths
      : [{ id: `month_${Date.now()}`, date: startDate, label: t('tools.timeline.newMonthLabel') }];

    onSave({
      name: (name || '').trim() || t('tools.timeline.title'),
      startDate,
      endDate: effectiveEndDate,
      months: safeMonths,
    });
  };

  return (
    <Modal
      title={t('tools.timeline.axisManagement')}
      open={true}
      onCancel={onClose}
      width={640}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('tools.timeline.cancel')}
        </Button>,
        <Button key="save" type="primary" disabled={!canSave} onClick={handleSave}>
          {t('tools.timeline.save')}
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <div>{t('tools.timeline.axisName')}</div>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <Space style={{ width: '100%' }}>
          <div style={{ flex: 1 }}>
            <div>{t('tools.timeline.axisStartDate')}</div>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <div>{t('tools.timeline.axisEndDate')}</div>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </Space>

        <div>{t('tools.timeline.monthLabels')}</div>
        {months.map((m) => (
          <Space key={m.id} style={{ width: '100%' }}>
            <Input
              style={{ flex: 1 }}
              type="date"
              value={m.date}
              onChange={(e) => updateMonth(m.id, { date: e.target.value })}
            />
            <Input
              style={{ flex: 1 }}
              value={m.label}
              onChange={(e) => updateMonth(m.id, { label: e.target.value })}
            />
            <Button danger onClick={() => removeMonth(m.id)}>
              {t('tools.timeline.delete')}
            </Button>
          </Space>
        ))}

        <Button onClick={addMonth}>{t('tools.timeline.addMonth')}</Button>
      </Space>
    </Modal>
  );
};

export default AxisEditor;
